/**
 * Main Router Configuration
 * Enterprise-level routing setup with lazy loading and route protection
 */
import { Routes, Route, Navigate } from 'react-router-dom';
import { Suspense, lazy } from 'react';

// Layouts
import MainLayout from '@/layouts/MainLayout';
import AuthLayout from '@/layouts/AuthLayout';

// Components
import ProtectedRoute from '@/components/auth/ProtectedRoute';
import Spinner from '@/components/common/Spinner';

// Route Configurations
import { publicRoutes } from './publicRoutes.jsx';
import { authRoutes } from './authRoutes.jsx';
import { protectedRoutes } from './protectedRoutes.jsx';

// Lazy load pages
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));

/**
 * Loading fallback component
 */
const LoadingFallback = () => (
  <div className="flex h-screen items-center justify-center">
    <Spinner size="lg" />
  </div>
);

/**
 * Main Application Router
 */
const AppRouter = () => {
  return (
    <Suspense fallback={<LoadingFallback />}>
      <Routes>
        {/* Public Routes */}
        <Route element={<MainLayout />}>
          {publicRoutes.map((route) => (
            <Route
              key={route.path}
              path={route.path}
              element={<route.component />}
            />
          ))}
        </Route>

        {/* Authentication Routes */}
        <Route element={<AuthLayout />}>
          {authRoutes.map((route) => (
            <Route
              key={route.path}
              path={route.path}
              element={<route.component />}
            />
          ))}
        </Route>

        {/* Protected Routes */}
        <Route
          element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          }
        >
          {protectedRoutes.map((route) => (
            <Route
              key={route.path}
              path={route.path}
              element={
                <ProtectedRoute roles={route.meta?.roles}>
                  <route.component />
                </ProtectedRoute>
              }
              index={route.index}
            />
          ))}
        </Route>

        {/* 404 Not Found */}
        <Route path="/404" element={<NotFoundPage />} />
        <Route path="*" element={<Navigate to="/404" replace />} />
      </Routes>
    </Suspense>
  );
};

export default AppRouter;
